// 调用硅基流动的 Qwen 视觉/文本模型

const API_URL = 'https://api.siliconflow.cn/v1/chat/completions'
const API_KEY = 'sk-wwcateithmifuqglzhzrkyzuqydnkybwiodsatrnlctlvdhu'

// 构造系统提示词：要求严格输出 JSON
function buildSystemPrompt() {
  return [
    '你是一位专业的英语教学内容生成助手。',
    '你需要根据用户提供的单词列表和要求，生成用于英语阅读训练的内容。',
    '必须严格按照给定的 JSON 结构输出，不要输出任何解释、Markdown 代码块或多余文字，只输出纯 JSON。',
    'JSON 结构如下：',
    `{
  "title": "文章英文标题",
  "category": "文章题材(中文，如 社会文化)",
  "sentences": [
    { "en": "英文句子", "zh": "该句中文翻译" }
  ],
  "keywords": [
    { "word": "文章中出现的目标单词(小写原形或文中形式)", "trans": "中文释义" }
  ],
  "questions": [
    {
      "title": "英文阅读理解题干",
      "options": [
        { "key": "A", "text": "选项英文内容" },
        { "key": "B", "text": "选项英文内容" },
        { "key": "C", "text": "选项英文内容" },
        { "key": "D", "text": "选项英文内容" }
      ],
      "answer": "正确选项的字母(如 B)",
      "analysis": "该题的中文解析"
    }
  ],
  "knowledge": [
    {
      "title": "知识点分组标题(中文，如 一、常用短语)",
      "items": [
        { "word": "短语或句型(英文)", "desc": "中文讲解" }
      ]
    }
  ]
}`,
    '要求：',
    '1. sentences 覆盖整篇文章，按顺序排列，拼接 en 即为完整文章。',
    '2. keywords 必须包含用户提供的所有目标单词，trans 为准确中文释义。',
    '3. questions 全部为四选一单选题，answer 必须是 options 中真实存在的字母。',
    '4. knowledge 至少包含"常用短语"和"句子结构"四组。',
    '5. 所有英文内容需符合指定难度，中文翻译准确自然。'
  ].join('\n')
}

// 构造用户提示词
function buildUserPrompt({ words, wordNum, questionNum, type, difficulty }) {
  const typeText = type === '随机' ? '不限题材，自行选择合适题材' : `题材：${type}`
  return [
    `请围绕以下英语单词生成一篇英语阅读短文：${words.join(', ')}。`,
    `难度：${difficulty}（请匹配该学段/考试的词汇与句式难度）。`,
    `文章字数：约 ${wordNum} 词。`,
    typeText + '。',
    `阅读理解题目数量：${questionNum} 道。`,
    '请让上述单词自然融入文章，并作为 keywords 输出释义。',
    '严格按系统要求的 JSON 结构返回，只返回 JSON。'
  ].join('\n')
}

// 从模型返回文本中提取 JSON（容错去除 ```json 包裹、尾逗号等）
function parseJSON(text) {
  if (!text) throw new Error('AI返回内容为空')
  let str = text.trim()
  // 去掉 markdown 代码块围栏
  str = str.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '')
  // 截取第一个 { 到最后一个 }
  const start = str.indexOf('{')
  const end = str.lastIndexOf('}')
  if (start !== -1 && end !== -1) {
    str = str.slice(start, end + 1)
  }
  try {
    return JSON.parse(str)
  } catch (e) {
    // 容错修复：去掉对象/数组结尾的多余逗号后再试一次
    const repaired = str.replace(/,\s*([}\]])/g, '$1')
    return JSON.parse(repaired)
  }
}

/**
 * 生成单篇阅读文章及配套内容
 * @param {object} params { words:[], wordNum, questionNum, type, difficulty }
 * @returns {Promise<object>} 结构化文章数据
 */
export async function generateReading(params, retry = 1) {
  const body = {
    // 换成你要用的千问模型
    model: 'Qwen/Qwen2.5-7B-Instruct',
    stream: false, // ⚠️ 注意这里必须是非流式
    temperature: 0.8,
    messages: [
      { role: 'system', content: buildSystemPrompt() },
      { role: 'user', content: buildUserPrompt(params) }
    ],
    // 💡 小提示：千问系列如果没有强制 JSON 格式，有时候会带 ```json 的包裹
    // 我们在 parseJSON 里已经做了容错处理，所以这里不需要 response_format
  }

  const resp = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${API_KEY}`
    },
    body: JSON.stringify(body)
  })

  if (!resp.ok) {
    const errData = await resp.json().catch(() => ({}))
    throw new Error(`硅基流动请求失败：${errData.message || resp.status}`)
  }

  const data = await resp.json()
  const content = data && data.choices && data.choices[0] &&
      data.choices[0].message && data.choices[0].message.content

  try {
    const parsed = parseJSON(content)
    return normalize(parsed)
  } catch (err) {
    // 解析失败（模型偶发输出坏 JSON）：自动重试一次
    if (retry > 0) {
      console.warn('JSON 解析失败，准备重试...')
      return generateReading(params, retry - 1)
    }
    throw err
  }
}

// 规整字段，补默认值，计算词数
function normalize(raw) {
  const sentences = Array.isArray(raw.sentences) ? raw.sentences : []
  const fullText = sentences.map(s => s.en || '').join(' ').trim()
  const wordCount = fullText ? fullText.split(/\s+/).length : 0
  return {
    title: raw.title || 'AI Reading',
    category: raw.category || '',
    wordCount,
    sentences,
    keywords: Array.isArray(raw.keywords) ? raw.keywords : [],
    questions: (Array.isArray(raw.questions) ? raw.questions : []).map(q => ({
      title: q.title || '',
      options: Array.isArray(q.options) ? q.options : [],
      answer: (q.answer || '').trim().toUpperCase(),
      analysis: q.analysis || '',
      chosen: ''
    })),
    knowledge: Array.isArray(raw.knowledge) ? raw.knowledge : []
  }
}
